/**
 * The active/all/inactive status quick filter every list page offers.
 *
 * One builder for the FilterBar def the grid pages each hand-wrote: a
 * single-select whose resting state is "Active" (value null), with "All"
 * and one inactive-state option ("Deactivated" or "Inactive" depending on
 * the entity), a chip label mirroring the selection, and an onChange that
 * maps the cleared filter back to 'active'.
 *
 * Contains:
 * - buildStatusQuickFilter — (opts) → FilterBar quick-filter def
 *
 * Returns plain JS — no JSX.
 */

/**
 * @param {object} opts
 * @param {Function} opts.t - i18next translator.
 * @param {'active'|'all'|string} opts.value - Current filter state; 'active'
 *   is the resting default.
 * @param {(value: string) => void} opts.onChange - Setter; receives 'active'
 *   when the chip is cleared.
 * @param {string} [opts.key] - Def key (default 'status'; billing uses 'active').
 * @param {string} [opts.inactiveValue] - The non-active option's value
 *   ('deactivated' by default; user-ish entities use 'inactive').
 * @param {string} [opts.inactiveLabel] - The non-active option's label
 *   (defaults to the translated "Deactivated").
 * @param {Array<{ value: string, label: string }>} [opts.extraOptions] -
 *   Further states after the inactive one (e.g. a "Deleted" view a role may
 *   open); their chip label is the option's own label.
 * @returns {object} A FilterBar quick-filter def.
 */
export function buildStatusQuickFilter({
  t,
  value,
  onChange,
  key = 'status',
  inactiveValue = 'deactivated',
  inactiveLabel,
  extraOptions = [],
}) {
  const inactive = inactiveLabel ?? t('general.deactivated');
  const options = [
    { value: 'all', label: t('general.all', 'All') },
    { value: inactiveValue, label: inactive },
    ...extraOptions,
  ];
  const selected = options.find(option => option.value === value);
  return {
    key,
    label: t('general.status'),
    type: 'single',
    allLabel: t('general.active', 'Active'),
    options,
    value: value === 'active' ? null : value,
    chipLabel: value === 'active' ? t('general.active', 'Active') : (selected?.label ?? inactive),
    onChange: v => onChange(v ?? 'active'),
  };
}
